import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  IconButton,
  Tooltip,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  ShoppingCart as OrderIcon,
  People as CustomerIcon,
  PointOfSale as SalesIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material'
import { QuickAction, QuickActionType } from '../../types/navigation'

interface QuickActionsProps {
  permissions: Record<string, boolean>
  onAction?: (actionType: QuickActionType) => void
}

const QuickActions: React.FC<QuickActionsProps> = ({ permissions, onAction }) => {
  const navigate = useNavigate()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const quickActions: QuickAction[] = [
    {
      id: QuickActionType.CREATE_SALES_ORDER,
      label: 'New Sales Order',
      icon: 'receipt',
      shortcut: 'Ctrl+O',
      permission: 'sales_orders.create',
      path: '/admin/documents/sales-orders/create',
      description: 'Create a new sales order'
    },
    {
      id: QuickActionType.TAKE_PAYMENT,
      label: 'Take Payment',
      icon: 'payment',
      shortcut: 'Ctrl+P',
      permission: 'payments.process',
      path: '/admin/payments/create',
      description: 'Record a new payment'
    },
    {
      id: QuickActionType.CREATE_CUSTOMER,
      label: 'New Customer',
      icon: 'customer',
      shortcut: 'Ctrl+Shift+C',
      permission: 'customers.create',
      path: '/admin/parties/customers/create',
      description: 'Add a new customer'
    }
  ]

  const getActionIcon = (iconType: string) => {
    switch (iconType) {
      case 'sales':
        return <SalesIcon />
      case 'order':
        return <OrderIcon />
      case 'receipt':
        return <ReceiptIcon />
      case 'payment':
        return <PaymentIcon />
      case 'customer':
        return <CustomerIcon />
      default:
        return <AddIcon />
    }
  }

  const checkPermission = (permId: string): boolean => {
    // If user is super admin (checked by parent or indicated by wildcard)
    if (permissions['*'] || permissions['admin.*']) return true
    return !!permissions[permId]
  }

  const handleActionClick = (action: QuickAction) => {
    if (onAction) {
      onAction(action.id)
    }
    navigate(action.path)
  }

  // Set up keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey || event.metaKey) {
        const action = quickActions.find(a => {
          const shortcutParts = a.shortcut.split('+')
          const key = shortcutParts[shortcutParts.length - 1].toLowerCase()
          const requiresShift = a.shortcut.includes('Shift')

          return event.key.toLowerCase() === key &&
            event.shiftKey === requiresShift &&
            checkPermission(a.permission)
        })

        if (action) {
          event.preventDefault()
          handleActionClick(action)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [permissions])

  const visibleActions = quickActions.filter(a => checkPermission(a.permission))

  if (isMobile) {
    // On mobile, show a single add button with menu
    return (
      <Tooltip title="Quick Actions">
        <IconButton
          color="inherit"
          onClick={() => navigate('/dashboard')}
          sx={{ mr: 1 }}
        >
          <AddIcon />
        </IconButton>
      </Tooltip>
    )
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
      {visibleActions.map((action) => (
        <Tooltip
          key={action.id}
          title={`${action.description} (${action.shortcut})`}
          arrow
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={getActionIcon(action.icon)}
            onClick={() => handleActionClick(action)}
            sx={{
              color: 'inherit',
              borderColor: 'rgba(255, 255, 255, 0.3)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
              minWidth: 'auto',
              px: 1.5,
            }}
          >
            {action.label}
          </Button>
        </Tooltip>
      ))}
    </Box>
  )
}

export default QuickActions